import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import { resetPassword } from '../services/authService';
import AuthLayout from '../components/AuthLayout';
import Input from '../components/Input';
import Button from '../components/Button';

const Title = styled.h1`
  font-size: 28px;
  margin-bottom: 8px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
`;

const Subtitle = styled.p`
  font-size: 16px;
  margin-bottom: 30px;
  color: #555;
  font-family: 'Montserrat', sans-serif;
`;

const FormContainer = styled.div`
  width: 100%;
`;

const InfoText = styled.div`
  margin-top: 24px;
  text-align: center;
  font-family: 'Montserrat', sans-serif;
`;

const StyledLink = styled(Link)`
  color: black;
  text-decoration: underline;
  font-weight: 600;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.p`
  color: #e53935;
  margin-bottom: 15px;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
`;

const SuccessMessage = styled.p`
  color: #43a047;
  margin-bottom: 15px;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
`;

const ResetPasswordScreen: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    
    // Clear messages when user types
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleResetPassword = async () => {
    // Validate email
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    try {
      setLoading(true);
      
      // Send password reset email
      await resetPassword(email);
      
      // Show success message
      setSuccess('Password reset email sent. Please check your inbox.');
      setEmail(''); // Clear email field
    } catch (error) {
      console.error('Password reset error:', error);
      
      // Handle specific error codes
      if (error instanceof Error) {
        if (error.message.includes('user-not-found')) {
          setError('No account found with this email address');
        } else {
          setError('Password reset failed: ' + error.message);
        }
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Reset Password</title>
        <meta name="description" content={`${APP_NAME} - Reset Password`} />
      </Helmet>
      <AuthLayout>
      <Title>Reset Password</Title>
      <Subtitle>Enter your email to receive a reset link</Subtitle>
      
      <FormContainer>
        <Input
          type="email"
          name="email"
          value={email}
          onChange={handleEmailChange}
          placeholder="Enter your email"
          label="Email Address"
          required
        />
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
        
        <Button 
          text="Send Reset Link"
          onClick={handleResetPassword}
          loading={loading}
          disabled={!email}
        />
      </FormContainer>
        <InfoText>
        Remember your password? <StyledLink to="/login">Back to Sign In</StyledLink>
      </InfoText>
    </AuthLayout>
    </>
  );
};

export default ResetPasswordScreen;